/**
 * MultiLineCurve — a clean multi-series value-over-time curve (dependency-free SVG,
 * house style). Each series is a line across shared x-positions (e.g. day offsets),
 * positioned PROPORTIONALLY on the x-axis so the real "fast early, then flattening"
 * LTV shape shows. Built for the founder's north-star view: day-by-day customer
 * value by source (2026-06-26). Nulls break the line (cohort not mature yet).
 */
import { useMemo } from "react";

const PALETTE = ["var(--accent)", "var(--c2)", "var(--accent-2)", "var(--good)", "var(--bad)", "var(--num)"];
const W = 640;
const H = 300;
const PAD = { l: 54, r: 14, t: 14, b: 34 };
const money0 = (n: number): string =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export interface CurveSeries { name: string; values: Array<number | null> }

/** 1→"1d", 30→"30d", 365→"12mo" — compact horizon labels. */
function dayLabel(d: number): string {
  if (d >= 360) return "12mo";
  if (d >= 180) return "6mo";
  return `${d}d`;
}

export function MultiLineCurve({ series, days, money = true, maxSeries = 6, onPick }: {
  series: CurveSeries[];
  /** x positions for each value index, ascending (e.g. [1,30,60,90,180,365]). */
  days: number[];
  money?: boolean;
  maxSeries?: number;
  onPick?: (name: string) => void;
}): React.ReactElement {
  const shown = useMemo(() => {
    const lastVal = (s: CurveSeries): number => {
      for (let i = s.values.length - 1; i >= 0; i--) { const v = s.values[i]; if (v !== null && v !== undefined) return v; }
      return 0;
    };
    return [...series].filter((s) => s.values.some((v) => v !== null && v !== undefined)).sort((a, b) => lastVal(b) - lastVal(a)).slice(0, maxSeries);
  }, [series, maxSeries]);

  if (shown.length === 0) return <div style={{ color: "var(--faint)", fontSize: 12.5, padding: "16px 0" }}>No data yet.</div>;

  const maxY = Math.max(1, ...shown.flatMap((s) => s.values.map((v) => v ?? 0)));
  const minD = days[0] ?? 0;
  const maxD = days[days.length - 1] ?? 1;
  const IW = W - PAD.l - PAD.r;
  const IH = H - PAD.t - PAD.b;
  const xOf = (d: number): number => PAD.l + ((d - minD) / (maxD - minD || 1)) * IW;
  const yOf = (v: number): number => PAD.t + IH - (v / maxY) * IH;
  const fmt = (v: number): string => (money ? money0(v) : Math.round(v).toLocaleString());

  const pathFor = (s: CurveSeries): string => {
    let d = "";
    let pen = false;
    s.values.forEach((v, i) => {
      if (v === null || v === undefined || days[i] === undefined) { pen = false; return; }
      d += `${pen ? "L" : "M"}${xOf(days[i]!).toFixed(1)},${yOf(v).toFixed(1)} `;
      pen = true;
    });
    return d.trim();
  };
  const lastPoint = (s: CurveSeries): { x: number; y: number } | null => {
    for (let i = s.values.length - 1; i >= 0; i--) { const v = s.values[i]; if (v !== null && v !== undefined && days[i] !== undefined) return { x: xOf(days[i]!), y: yOf(v) }; }
    return null;
  };

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block" }} role="img" aria-label="Customer value over time by source">
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const y = PAD.t + IH - f * IH;
          return (
            <g key={f}>
              <line x1={PAD.l} y1={y} x2={W - PAD.r} y2={y} stroke="var(--border)" strokeWidth={0.5} />
              <text x={PAD.l - 6} y={y + 3} textAnchor="end" fontSize={10} fill="var(--faint)">{fmt(f * maxY)}</text>
            </g>
          );
        })}
        {days.map((d) => (
          <text key={d} x={xOf(d)} y={H - PAD.b + 17} textAnchor="middle" fontSize={10} fill="var(--faint)">{dayLabel(d)}</text>
        ))}
        {shown.map((s, i) => (
          <path key={s.name} d={pathFor(s)} fill="none" stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        ))}
        {shown.map((s, i) => {
          const p = lastPoint(s);
          return p ? <circle key={s.name} cx={p.x} cy={p.y} r={3} fill={PALETTE[i % PALETTE.length]} /> : null;
        })}
      </svg>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", marginTop: 8, fontSize: 11.5 }}>
        {shown.map((s, i) => (
          <button key={s.name} type="button" onClick={onPick ? () => onPick(s.name) : undefined}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "none", border: "none", padding: 0, cursor: onPick ? "pointer" : "default", color: "var(--body)", font: "inherit" }}>
            <i style={{ display: "inline-block", width: 11, height: 3, borderRadius: 2, background: PALETTE[i % PALETTE.length] }} />
            {s.name}
          </button>
        ))}
      </div>
    </div>
  );
}
